import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { throttle } from "lodash";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import LinearProgress from "@material-ui/core/LinearProgress";
import MenuItem from "@material-ui/core/MenuItem";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import gql from "graphql-tag";
import { stringify } from "qs";
import { DateTime } from "luxon";

import { downloadWithAuth } from "../../../lib/authManager";

const DRIVO_URL = process.env.REACT_APP_API_URL;

export const transactionsQuery = gql`
  query Transactions($input: QueryChargeByDateInput!) {
    getChargesByDate(input: $input) {
      message
      stripeCount
      questCount
      stripeAmountTotal
      questAmountTotal
      stripeCharges {
        stripeChargeId amountAud amountAudHotel amountAudService createdAt
        bookingId guestName guestEmail plate booking_start booking_end approved
        requestedRefundAt processedRefundAt
      }
      questCharges {
        charges { id ref GatewayCreatedAt amount plate approved gateName }
      }
    }
  }
`;

const PAGE_SIZES = [25, 50, 100];
const useStyles = makeStyles({
  root: { marginTop: 16 },
  controls: { display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 12, marginBottom: 12 },
  control: { minWidth: 160 },
  pageSizeControl: { minWidth: 140 },
  tabs: { display: "flex", gap: 8, alignItems: "center", marginBottom: 12 },
  tab: { textTransform: "none", borderRadius: 6 },
  activeTab: { background: "#3127B8", color: "#fff", "&:hover": { background: "#241C8A" } },
  download: { marginLeft: "auto", textTransform: "none" },
  tableWrap: { overflowX: "auto", border: "1px solid var(--color-border-tertiary, #E1E4EA)", borderRadius: 8 },
  header: { fontWeight: 600, color: "var(--color-text-secondary)", whiteSpace: "nowrap" },
  row: {
    "& td": { borderBottom: "1px solid var(--color-border-tertiary, #E1E4EA)" },
  },
  clickableRow: {
    cursor: "pointer",
    "&:hover td": { backgroundColor: "var(--color-background-secondary, #F7F8FA)" },
  },
  cell: { whiteSpace: "nowrap" },
  footer: { display: "flex", alignItems: "center", gap: 10, padding: "12px 4px 0" },
  total: { marginLeft: "auto", fontWeight: 700, fontVariantNumeric: "tabular-nums" },
  empty: { padding: 28, textAlign: "center", color: "var(--color-text-secondary)" },
});

function formatDate(value, zone) {
  if (!value) return "-";
  const parsed = /^\d+$/.test(String(value))
    ? DateTime.fromMillis(Number(value), { zone: "utc" })
    : DateTime.fromISO(String(value), { zone: "utc" });
  return parsed.isValid ? parsed.setZone(zone || "utc").toFormat("d MMM yyyy HH:mm") : "-";
}

function statusLabel(charge, source) {
  if (source === "stripe" && charge.requestedRefundAt && charge.processedRefundAt) return "Refunded";
  return String(charge.approved) === "true" ? "Approved" : "Failed";
}

export default function TransactionList({
  installation,
  installationId,
  startDate,
  endDate,
  displayZone,
  embedded = false,
  showGateCharges = true,
}) {
  const classes = useStyles();
  const [source, setSource] = useState("stripe");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const abortRef = useRef(null);
  const [fetchTransactions, { data, loading, error }] = useLazyQuery(transactionsQuery, {
    fetchPolicy: "network-only",
  });
  const fetchTransactionsRef = useRef(fetchTransactions);
  fetchTransactionsRef.current = fetchTransactions;
  const updateSearch = useMemo(() => throttle(setDebouncedSearch, 350), []);

  useEffect(() => () => updateSearch.cancel(), [updateSearch]);
  useEffect(() => {
    if (!showGateCharges && source === "quest") setSource("stripe");
  }, [showGateCharges, source]);
  useEffect(() => {
    updateSearch(search);
  }, [search, updateSearch]);
  useEffect(() => setPage(0), [source, debouncedSearch, status, pageSize, startDate, endDate, installationId]);

  useEffect(() => {
    if (!startDate || !endDate) return undefined;
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    Promise.resolve(fetchTransactionsRef.current({
      variables: {
        input: {
          installationId: installationId || undefined,
          startDate,
          endDate,
          source,
          search: debouncedSearch || undefined,
          status: status || undefined,
          offset: page * pageSize,
          limit: pageSize,
        },
      },
      context: { fetchOptions: { signal: controller.signal } },
    })).catch(() => undefined);
    return () => controller.abort();
  }, [debouncedSearch, endDate, installationId, page, pageSize, source, startDate, status]);

  const result = data?.getChargesByDate;
  const rows = source === "stripe"
    ? result?.stripeCharges || []
    : result?.questCharges?.charges || [];
  const count = source === "stripe" ? result?.stripeCount || 0 : result?.questCount || 0;
  const total = source === "stripe" ? result?.stripeAmountTotal || 0 : result?.questAmountTotal || 0;
  const pageCount = Math.max(1, Math.ceil(count / pageSize));
  const first = count ? page * pageSize + 1 : 0;
  const last = Math.min(count, (page + 1) * pageSize);
  const exportParams = {
    installationId: installationId || undefined,
    startDate,
    endDate,
    search: debouncedSearch || undefined,
    status: status || undefined,
  };

  const handleDownload = async () => downloadWithAuth({
    url: `${DRIVO_URL}/transactions/download?${stringify(exportParams)}`,
    filename: `transactions_${startDate.slice(0, 10)}_${endDate.slice(0, 10)}_${installation?.brandingKey || "all"}.xlsx`,
  });

  const openBooking = (charge) => {
    if (source !== "stripe" || !installation?.brandingKey || !charge.bookingId) return;
    window.open(
      `/${installation.brandingKey}/bookings/${charge.bookingId}`,
      "_blank",
      "noopener,noreferrer",
    );
  };

  return (
    <Paper elevation={embedded ? 0 : 1} className={classes.root} style={{ padding: embedded ? 0 : 16 }}>
      <Box className={classes.controls}>
        <TextField className={classes.control} size="small" variant="outlined" label="Search" value={search} onChange={(event) => setSearch(event.target.value)} />
        <TextField className={classes.control} size="small" variant="outlined" select label="Charge status" value={status} onChange={(event) => setStatus(event.target.value)}>
          <MenuItem value="">All statuses</MenuItem>
          <MenuItem value="approved">Approved</MenuItem>
          <MenuItem value="failed">Failed</MenuItem>
          <MenuItem value="refunded">Refunded</MenuItem>
        </TextField>
        <TextField className={classes.pageSizeControl} size="small" variant="outlined" select label="Rows per page" value={pageSize} onChange={(event) => setPageSize(Number(event.target.value))}>
          {PAGE_SIZES.map((size) => <MenuItem key={size} value={size}>{size}</MenuItem>)}
        </TextField>
      </Box>
      <Box className={classes.tabs}>
        <Button aria-pressed={source === "stripe"} className={`${classes.tab} ${source === "stripe" ? classes.activeTab : ""}`} onClick={() => setSource("stripe")}>Stripe charges</Button>
        {showGateCharges && <Button aria-pressed={source === "quest"} className={`${classes.tab} ${source === "quest" ? classes.activeTab : ""}`} onClick={() => setSource("quest")}>Gate charges</Button>}
        <Button className={classes.download} variant="outlined" onClick={handleDownload}>Download</Button>
      </Box>
      {loading && <LinearProgress aria-label="Loading transactions" />}
      {error || (result?.message && result.message !== "success") ? (
        <Box className={classes.empty}>
          {error?.networkError?.statusCode === 403
            || (error?.graphQLErrors || []).some((item) => item.message === "403")
            ? "You do not have access to transactions."
            : "Could not load transactions. Please try again or contact support."}
        </Box>
      ) : (
        <div className={classes.tableWrap}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell className={classes.header}>Reference</TableCell>
                <TableCell className={classes.header}>Created at</TableCell>
                {source === "stripe" && <TableCell className={classes.header}>Guest</TableCell>}
                <TableCell className={classes.header}>Plate</TableCell>
                {source === "quest" && <TableCell className={classes.header}>Gate</TableCell>}
                <TableCell className={classes.header}>Status</TableCell>
                <TableCell className={classes.header} align="right">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((charge, index) => {
                const isBookingRow = Boolean(source === "stripe" && installation?.brandingKey && charge.bookingId);
                return (
                  <TableRow
                    key={charge.stripeChargeId || charge.id || charge.ref || index}
                    className={`${classes.row} ${isBookingRow ? classes.clickableRow : ""}`}
                    hover
                    role={isBookingRow ? "button" : undefined}
                    tabIndex={isBookingRow ? 0 : undefined}
                    onClick={() => openBooking(charge)}
                    onKeyDown={isBookingRow ? (event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        openBooking(charge);
                      }
                    } : undefined}
                  >
                  <TableCell className={classes.cell}>{charge.stripeChargeId || charge.ref || charge.id}</TableCell>
                  <TableCell className={classes.cell}>{formatDate(charge.createdAt || charge.GatewayCreatedAt, displayZone)}</TableCell>
                  {source === "stripe" && <TableCell className={classes.cell}>{charge.guestName || charge.guestEmail || "-"}</TableCell>}
                  <TableCell className={classes.cell}>{charge.plate || "-"}</TableCell>
                  {source === "quest" && <TableCell className={classes.cell}>{charge.gateName || "-"}</TableCell>}
                  <TableCell className={classes.cell}>{statusLabel(charge, source)}</TableCell>
                  <TableCell className={classes.cell} align="right">${(source === "stripe" ? Number(charge.amountAud) : Number(charge.amount) / 100).toFixed(2)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {!loading && rows.length === 0 && <Box className={classes.empty}>No transactions match the selected filters.</Box>}
        </div>
      )}
      <Box className={classes.footer}>
        <Typography variant="caption">Showing {first} to {last} of {count}</Typography>
        <Button size="small" disabled={page === 0} onClick={() => setPage((value) => value - 1)}>Previous</Button>
        <Button size="small" disabled={page + 1 >= pageCount} onClick={() => setPage((value) => value + 1)}>Next</Button>
        <Typography variant="body2" className={classes.total}>Total: ${Number(total).toFixed(2)}</Typography>
      </Box>
    </Paper>
  );
}
